import { buildSelectionForMode, dataElementKey } from "../modal/consentSelection";
import { ASSISTED_CONFIG, DEFAULT_PURPOSE_PRESELECTION, EMAIL_REGEX } from "./constants";
import type { Purpose, PurposeItemData } from "../api/types";
import type { AcceptMode, SelectablePurpose } from "../types";
import { ApiError } from "../shared/api-errors";
import type { AssistedSelection, PurposePreselection, StaticKey, VerifyMethod } from "./types";

export const isBlockingRequiredElement = (element: {
  enabled: boolean;
  required: boolean;
}): boolean => element.required && element.enabled;

export const hasBlockingRequiredElement = (purpose: SelectablePurpose): boolean =>
  purpose.data_elements.some(isBlockingRequiredElement);

export const resolvePurposePreselection = (
  raw: string | undefined | null
): PurposePreselection =>
  raw === "MANDATORY" || raw === "ALL" ? raw : DEFAULT_PURPOSE_PRESELECTION;

export const buildPreselection = (
  purposes: ReadonlyArray<SelectablePurpose>,
  mode: PurposePreselection
): AssistedSelection => {
  const empty: AssistedSelection = { selectedPurposes: {}, selectedDataElements: {} };
  if (mode === "ALL") {
    return buildSelectionForMode(purposes.map((purpose) => ({ purpose })), "all", empty);
  }

  purposes.forEach((purpose) => {
    let hasPreselectedElement = false;
    purpose.data_elements.forEach((element) => {
      const isPreselected = mode === "MANDATORY" && isBlockingRequiredElement(element);
      empty.selectedDataElements[dataElementKey(purpose.uuid, element.uuid)] = isPreselected;
      hasPreselectedElement = hasPreselectedElement || isPreselected;
    });
    empty.selectedPurposes[purpose.uuid] = hasPreselectedElement;
  });

  return empty;
};

export const togglePurposeSelection = (
  purpose: SelectablePurpose,
  current: AssistedSelection
): AssistedSelection => {
  const next = !current.selectedPurposes[purpose.uuid];
  const selectedDataElements = { ...current.selectedDataElements };
  purpose.data_elements.forEach((element) => {
    selectedDataElements[dataElementKey(purpose.uuid, element.uuid)] = next && element.enabled;
  });
  return {
    selectedPurposes: { ...current.selectedPurposes, [purpose.uuid]: next },
    selectedDataElements,
  };
};

export const toggleDataElementSelection = (
  purpose: SelectablePurpose,
  elementUuid: string,
  current: AssistedSelection
): AssistedSelection => {
  const key = dataElementKey(purpose.uuid, elementUuid);
  const selectedDataElements = {
    ...current.selectedDataElements,
    [key]: !current.selectedDataElements[key],
  };
  const isTicked = (element: { uuid: string }) =>
    Boolean(selectedDataElements[dataElementKey(purpose.uuid, element.uuid)]);
  const blocking = purpose.data_elements.filter(isBlockingRequiredElement);
  const purposeSelected =
    blocking.length > 0
      ? blocking.every(isTicked)
      : purpose.data_elements.some((element) => element.enabled && isTicked(element));
  return {
    selectedPurposes: { ...current.selectedPurposes, [purpose.uuid]: purposeSelected },
    selectedDataElements,
  };
};

export const areAllRequiredElementsChecked = (
  purposes: ReadonlyArray<SelectablePurpose>,
  selectedDataElements: Record<string, boolean>
): boolean =>
  purposes.every((purpose) =>
    purpose.data_elements
      .filter(isBlockingRequiredElement)
      .every((element) => Boolean(selectedDataElements[dataElementKey(purpose.uuid, element.uuid)]))
  );

export const selectionForMode = (
  purposes: ReadonlyArray<SelectablePurpose>,
  mode: AcceptMode,
  current: AssistedSelection
): AssistedSelection =>
  buildSelectionForMode(purposes.map((purpose) => ({ purpose })), mode, current);

export const buildAssistedSubmitPurposes = (
  purposes: ReadonlyArray<PurposeItemData>,
  selection: AssistedSelection
): Purpose[] =>
  purposes.map((purpose) => ({
    uuid: purpose.uuid,
    name: purpose.name,
    description: purpose.description,
    selected: Boolean(selection.selectedPurposes[purpose.uuid]),
    data_elements: purpose.data_elements.map((element) => ({
      uuid: element.uuid,
      name: element.name,
      enabled: element.enabled !== false,
      required: element.required,
      selected: isBlockingRequiredElement(element)
        ? true
        : Boolean(selection.selectedDataElements[dataElementKey(purpose.uuid, element.uuid)]),
    })),
  }));

export const sanitiseMobile = (raw: string): string =>
  raw.replace(/\D/g, "").slice(0, ASSISTED_CONFIG.mobileDigits);

export const isContactValid = (method: VerifyMethod, mobile: string, email: string): boolean =>
  method === "email"
    ? EMAIL_REGEX.test(email.trim())
    : mobile.replace(/\D/g, "").length >= ASSISTED_CONFIG.mobileDigits;

export const otpRecipient = (method: VerifyMethod, mobile: string, email: string): string =>
  method === "email" ? email.trim() : `${ASSISTED_CONFIG.dialCode}${mobile}`;

export const otpChannel = (method: VerifyMethod): string =>
  method === "email" ? ASSISTED_CONFIG.emailChannel : ASSISTED_CONFIG.channel;

export const emptyOtpDigits = (): string[] =>
  Array.from({ length: ASSISTED_CONFIG.otpLength }, () => "");

export const isOtpComplete = (digits: ReadonlyArray<string>): boolean =>
  digits.join("").length === ASSISTED_CONFIG.otpLength;

export const applyOtpDigit = (
  digits: ReadonlyArray<string>,
  index: number,
  raw: string
): { digits: string[]; focusIndex: number | null } => {
  const digit = raw.replace(/\D/g, "").slice(-1);
  const next = [...digits];
  next[index] = digit;
  const focusIndex = digit && index < ASSISTED_CONFIG.otpLength - 1 ? index + 1 : null;
  return { digits: next, focusIndex };
};

export const applyOtpPaste = (text: string): { digits: string[]; focusIndex: number } | null => {
  const pasted = text.replace(/\D/g, "").slice(0, ASSISTED_CONFIG.otpLength);
  if (!pasted) return null;
  return {
    digits: Array.from({ length: ASSISTED_CONFIG.otpLength }, (_, i) => pasted[i] ?? ""),
    focusIndex: Math.min(pasted.length, ASSISTED_CONFIG.otpLength - 1),
  };
};

export const backspaceFocusIndex = (digits: ReadonlyArray<string>, index: number): number | null =>
  !digits[index] && index > 0 ? index - 1 : null;

export const verifyFailureKey = (error: unknown): StaticKey => {
  if (error instanceof ApiError && error.status === 404) return "otpNotFound";
  if (error instanceof ApiError && error.status === 400) {
    return error.shouldRequestNew ? "otpExpired" : "otpIncorrect";
  }
  return "otpVerifyFailed";
};

export const isAlreadyConsentedError = (error: unknown): boolean =>
  error instanceof ApiError && (error.status === 409 || error.code === "CONSENT_ALREADY_PROVIDED");

export const purposeHidingElement = (
  purposes: ReadonlyArray<PurposeItemData>,
  collapsedPurposes: Record<string, boolean>,
  elementId: string | null
): string | null => {
  if (!elementId) return null;
  const owner = purposes.find((purpose) =>
    purpose.data_elements.some(
      (element) =>
        element.enabled !== false && elementId === `data-element-${purpose.uuid}-${element.uuid}`
    )
  );
  return owner && collapsedPurposes[owner.uuid] ? owner.uuid : null;
};
